/* eslint-disable no-use-before-define */
import {
  IntegrationTrigger,
  FirebaseTimestamp,
  EPublicNotice,
  EInvite,
  Note,
  Notarization
} from '.';
import { ENotice } from './notice';
import { ERef } from './firebase';
import { AffidavitReconciliationSettings, EOrganization } from './organization';
import {
  EInvoice,
  EventInvoiceData,
  OrderInvoice,
  PaymentMethods,
  PublicNoticeInvoice
} from './invoices';
import { EUser } from './user';
import { EIntegrationTrigger } from './integrations';
import { EDeadline } from './deadlines';
import { EData } from './data';
import { AffinityXOrderNumber } from '../integrations/affinityx/types';
import { EEdition } from './eedition';
import { JSONObject } from './logger';
import { Run, RunStatusType } from './runs';
import { PublicationIssue, PublicationIssueStatus } from './publicationIssue';
import { PublicationIssueAttachment } from './publicationIssueAttachment';
import { EventRefDocumentType } from '../services/eventService';
import { ElasticDocumentType } from './searchable';
import { Order } from './order';
import { PublicationIssueSection } from './publicationIssueSection';
import { NotarizationStatus } from './notarization';
import { BriefBatchCadence } from './briefBatch';
import { LedgerItem, LedgerItemStatus } from './ledger';
import { Product, OccupationType } from '../enums';
import { NewspaperOrder } from './newspaperOrder';
import { AdjudicationArea } from './adjudicationArea';
import { PaperCheck } from './paperCheck';
import { FrontEvent } from './emailAutomation/front';
import { AutomaticBulkDownloadRequest } from './requests';
import {
  PlacementRequestData,
  PublisherSelection
} from './emailAutomation/placement';

export const NOTICE_SYNC_EVENT = 'notices.sync';
export const NOTICE_SYNC_FAILURE_EVENT = 'notices.sync_failed';
export const NOTICE_SYNC_RESPONSE = 'notices.sync_response';
export const NOTICE_SYNC_RESPONSE_FILE_RECEIVED_EVENT =
  'notices.sync_response.file_received';
export const NOTICE_SUBMITTED_EVENT = 'notices.submitted';
export const INVOICE_MAIL_SENT_EVENT = 'invoices.mail_sent';
export const NOTICE_AT_DEADLINE = 'notices.at_deadline';
export const NOTICE_CREATED = 'notices.created';
export const NOTICE_DATA_EXTRACTED = 'notices.data.extracted';
export const MANUAL_SYNC_REQUEST = 'notices.manual_sync_request';
export const APPROVE_BUILT_AD_REQUEST = 'notices.approve_built_ad_request';
export const NOTICES_BUILT_AD_REVIEW_REQUEST =
  'notices.built_ad.review_request';
export const MANUAL_BUILD_AD_REQUEST = 'notices.build_ad.manual_build_request';
export const AUTOMATED_BUILD_AD_REQUEST =
  'notices.build_ad.automated_build_request';
export const MANUAL_CANCEL_BUILD_AD_REQUEST =
  'notices.build_ad.manual_cancel_request';

export const NOTICE_NEWSPAPER_REQUESTED = 'notices.newspaper.requested';
export const NOTICE_CONFIRMED = 'notices.confirmed';
export const NOTICE_EDITED = 'notices.edited';
export const NOTICE_CANCELLED = 'notices.cancelled';
export const NOTICE_CANCELLATION_REQUESTED = 'notices.cancellation.requested';
export const AFFIDAVIT_UPLOADED = 'notices.affidavit.uploaded';
export const AFFIDAVIT_UPLOAD_REJECTED = 'notices.affidavit.rejected';
export const CUSTOM_ID_UPDATED = 'notices.custom_id.updated';
export const DESIGN_NOTES_UPDATED = 'notices.design_notes.updated';
export const AFFIDAVIT_RE_UPLOADED = 'notices.affidavit.reuploaded';
export const NOTICE_TRANSFERRED = 'notices.transferred';
export const NOTICE_REVERSE_TRANSFER_FEE = 'notices.reverse_transfer.fee';
export const NOTICE_REVERSE_TRANSFER_REFUND = 'notices.reverse_transfer.refund';
export const NOTICE_NOTE_ADDED = 'notices.note.added';

export const NOTICE_AFFILIATE_CLICKED = 'notices.affiliate.clicked';

export const NOTICE_ASYNC_DESIGN_FINALIZED = 'notices.asyncdesign.finalized';

export const NOTICE_RECONCILE_RUNS = 'notices.reconcile.runs';

export const RUN_STATUS_CHANGE = 'runs.status_change';

export const NOTARIZATION_STATUS_CHANGE = 'notarization.status_change';

export const LEDGER_ITEM_STATUS_CHANGE = 'ledger.status_change';
export const LEDGER_ITEM_RECEIPT_READY = 'ledger.receipt_ready';

export const PUBLISHER_GLOBAL_RATE_SETTINGS_UPDATED =
  'settings.publisher.global_rates.updated';
export const PUBLISHER_ADDITIONAL_FEE_SETTINGS_UPDATED =
  'settings.publisher.cross_paper_fee.updated';

export const USER_REGISTERED = 'user.registered';

export const INVOICE_CREATED = 'invoices.created';
export const INVOICE_CANCELLED = 'invoices.cancelled';
export const INVOICE_REFUNDED = 'invoices.refunded';
export const INVOICE_RE_CREATED = 'invoices.recreated';
export const INVOICE_REMINDER_SENT = 'invoices.reminder.sent';
export const INVOICE_PAYMENT_PROCESSED = 'invoice.paid';
export const INVOICE_PAYMENT_INITIATED = 'invoice.initiated';
export const INVOICE_PAYMENT_AUTHORIZED = 'invoice.authorized';
export const INVOICE_PAYMENT_PROCESSED_MANUAL = 'invoice.paid.manual';
export const INVOICE_PAYMENT_NOTE = 'invoice.paid.note';
export const INVOICE_PAID_OUTSIDE = 'invoice.paid_outside';
export const INVOICE_CREATION_INITIATED = 'invoice.creation.initiated';
export const INVOICE_CREATION_FAILED = 'invoice.creation.failed';
export const INVOICE_UPFRONT_PAYMENT_WAIVER = 'invoice.upfront_payment_waiver';

// Order event types
export const ORDER_INVOICE_REFUNDED = 'order.invoices.refunded';
export const ORDER_INVOICE_CREATED = 'order.invoices.created';
export const ORDER_INVOICE_PAID = 'order.invoices.paid';
export const ORDER_INVOICE_PAID_OUT_OF_PLATFORM =
  'order.invoices.paid_out_of_platform';
export const ORDER_INVOICE_PAYMENT_NOTE = 'order.invoices.paid.note';
export const ORDER_EDITED = 'order.edited';
export const ORDER_CANCELLED = 'order.cancelled';
export const ORDER_OBITUARY_VERIFIED = 'order.obituary.verified';
export const ORDER_NEWSPAPER_ORDER_CONFIRMED =
  'order.newspaper_order.confirmed';
export const ORDER_NEWSPAPER_ORDER_COMPLETED =
  'order.newspaper_order.completed';
export const ORDER_NEWSPAPER_ORDER_DELETED = 'order.newspaper_order.deleted';
export const ORDER_NEWSPAPER_ORDER_AT_DEADLINE =
  'order.newspaper_order.at_deadline';
export const ORDER_MANUAL_SYNC_REQUEST = 'order.manual_sync_request';

export const NOTICE_CANCELLED_AND_REFUNDED = 'invoices.cancelled.refunded';

export const NEWSPAPER_AT_DEADLINE = 'newspaper.at_deadline';
export const NEWSPAPER_EXTERNAL_UPLOAD = 'newspaper.external_upload';
export const NEWSPAPER_AUTOMATIC_BULK_EXPORT =
  'newspaper.automatic_bulk_export';
export const DEADLINE_OVERRIDE_EVENT = 'newspaper.deadline_overriden';

export const REALTIME_ELASTIC_SYNC = 'elastic.sync';

export const AFFIDAVIT_NOTARIZATION_INITIATED =
  'affidavit.notarization.initiated';
export const AFFIDAVIT_NOTARIZATION_SUCCESS = 'affidavit.notarization.success';
export const AFFIDAVIT_NOTARIZATION_FAILED = 'affidavit.notarization.failed';

export const INVITATION_CREATED = 'invites.created';

export const AUTO_EEDITION_UPLOAD_SUCCESS = 'affidavit.eedition.success';
export const AFFIDAVIT_EEDITION_SCRAPER_INITIATED =
  'affidavit.eedition.scraper.initiated';

export const DISPLAY_SITE_UPLOAD = 'display_site.upload';

export const EEDITION_UPLOADED = 'eedition.uploaded';

export const BRIEF_BATCH_PDFS_UPLOADED = 'briefBatch.pdfs.uploaded';

export const INBOX_AUTOMATION_DRAFT_REQUEST = 'inbox.automation.draft.request';
export const INBOX_AUTOMATION_DRAFT_CREATED = 'inbox.automation.draft.created';
export const INBOX_AUTOMATION_CONTEXT_CALL = 'inbox.automation.context.call';
export const INBOX_AUTOMATION_DRAFT_SENT = 'inbox.automation.draft.sent';

export const INBOX_AUTOMATION_NOTICE_UPLOAD_REQUEST =
  'inbox.automation.notice.upload.request';
export const INBOX_AUTOMATION_CANCELLATION_REQUEST =
  'inbox.automation.cancellation.request';
export const INBOX_AUTOMATION_CONFIRM_PUBLICATION_REQUEST =
  'inbox.automation.confirm.publication.request';
export const INBOX_AUTOMATION_AFFIDAVIT_REQUEST =
  'inbox.automation.affidavit.request';
export const INBOX_AUTOMATION_AFFIDAVIT_UPLOAD =
  'inbox.automation.affidavit.upload';
export const INBOX_AUTOMATION_INTENT_REQUEST =
  'inbox.automation.intent.request';
export const INBOX_AUTOMATION_INTENT_TEARSHEET_REQUEST =
  'inbox.automation.intent.tearsheet.request';
export const INBOX_AUTOMATION_INTENT_AFFIDAVIT_REQUEST =
  'inbox.automation.intent.affidavit.request';
export const INBOX_AUTOMATION_INTENT_INVOICE_REQUEST =
  'inbox.automation.intent.invoice.request';
export const INBOX_AUTOMATION_INTENT_PROOF_REQUEST =
  'inbox.automation.intent.proof.request';
export const INBOX_AUTOMATION_INTENT_PUBLICATION_DATES_REQUEST =
  'inbox.automation.intent.publication_dates.request';
export const INBOX_AUTOMATION_INTENT_NEW_PUBLICATION_REQUEST =
  'inbox.automation.intent.new_publication.request';

export const INBOX_AUTOMATION_LOG_EVENT = 'inbox.automation.log';
export const INBOX_AUTOMATION_TRIGGER_EVENT = 'inbox.automation.trigger';

export const ADMIN_OPERATION = 'admin.operation';

export const TRANSACTIONAL_EMAIL_EVENT = 'email.transactional_event';

export const PAPER_CHECK_CREATED = 'paper_check.created';

export const PUBLICATION_ISSUE_STATUS_CHANGE =
  'publication_issue.status_change';
export const PUBLICATION_ISSUE_READY_FOR_PAGINATION =
  'publication_issue.ready_for_pagination';
export const PUBLICATION_ISSUE_SECTION_STATUS_CHANGE =
  'publication_issue_section.status_change';

export const RUN_VERIFICATION_INITIATED = 'run.verification.initiated';
export const STRIPE_ID_UPDATED = 'stripeid.updated';
export const EMAIL_UPDATED = 'email.updated';
export const INVOICE_REMINDER_DUE = 'invoice.reminder.due';

export const ADJUDICATION_AREA_SET = 'adjudication.area.set';

export const MONTHLY_STATEMENT_EVENT = 'statements.monthly.create';

export const PLACEMENT_FEE_BILLING_TRIGGER = 'placement.fee.billing.trigger';

export const STRIPE_OPERATION = 'stripe.operation';

export const PUBLIC_API_PLACEMENT_REQUEST = 'public.api.placement.request';

export enum PublicApiPlacementRequestStatus {
  QUEUED = 'queued',
  COMPLETE = 'complete',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export interface PublicApiPlacementRequestEvent extends ProcessableEventBase {
  type: typeof PUBLIC_API_PLACEMENT_REQUEST;
  data: {
    status: PublicApiPlacementRequestStatus;
    tenant: string;
    /** Present on requests created after idempotency support was added. */
    idempotencyKey?: string;
    /**
     * Set while a cancellation is in flight and cleared if it fails.
     */
    cancellationClaimedAt?: FirebaseTimestamp;
    conversationId?: string;
    request: {
      state: string;
      county: string;
      publisher?: string;
      courtDocument?: string;
      noticeHtml?: string;
      metadata?: Record<string, string>;
      publicationDates?: string[];
      filingType?: 'DOMESTIC_LLC' | 'FOREIGN_LLC';
      madlib?: Record<string, string>;
    };
    response: {
      noticeId: string | null;
      noticeIds?: string[];
      error: string | null;
    };
  };
}

export enum StripeOperationType {
  APPLICATION_FEE_INVOICE = 'application_fee_invoice',
  PAPER_CHECK_RECONCILIATION = 'paper_check_reconciliation',
  PAYMENT_PROCESSING_FEE = 'payment_processing_fee',
  ORDER_PAYMENT_PROCESSING_FEE = 'order_payment_processing_fee',
  NOTICE_INVOICE_CREATION = 'notice_invoice_creation',
  ORDER_INVOICE_CREATION = 'order_invoice_creation'
}

export const SYNC_TRIGGER_EVENT_TYPES: SyncTriggerEvent['type'][] = [
  CUSTOM_ID_UPDATED,
  INVOICE_CREATED,
  INVOICE_PAID_OUTSIDE,
  INVOICE_PAYMENT_INITIATED,
  INVOICE_PAYMENT_PROCESSED_MANUAL,
  INVOICE_PAYMENT_PROCESSED,
  INVOICE_REFUNDED,
  INVOICE_UPFRONT_PAYMENT_WAIVER,
  MANUAL_BUILD_AD_REQUEST,
  MANUAL_CANCEL_BUILD_AD_REQUEST,
  MANUAL_SYNC_REQUEST,
  NOTICE_AT_DEADLINE,
  NOTICE_CANCELLED,
  NOTICE_CONFIRMED,
  NOTICE_CREATED,
  NOTICE_EDITED
];

export const CUSTOM_ID_UPDATED_SYNC = `${CUSTOM_ID_UPDATED}.sync` as const;
export const INVOICE_CREATED_SYNC = `${INVOICE_CREATED}.sync` as const;
export const INVOICE_PAID_OUTSIDE_SYNC =
  `${INVOICE_PAID_OUTSIDE}.sync` as const;
export const INVOICE_PAYMENT_INITIATED_SYNC =
  `${INVOICE_PAYMENT_INITIATED}.sync` as const;
export const INVOICE_PAYMENT_PROCESSED_MANUAL_SYNC =
  `${INVOICE_PAYMENT_PROCESSED_MANUAL}.sync` as const;
export const INVOICE_PAYMENT_PROCESSED_SYNC =
  `${INVOICE_PAYMENT_PROCESSED}.sync` as const;
export const INVOICE_REFUNDED_SYNC = `${INVOICE_REFUNDED}.sync` as const;
export const INVOICE_UPFRONT_PAYMENT_WAIVER_SYNC =
  `${INVOICE_UPFRONT_PAYMENT_WAIVER}.sync` as const;
export const MANUAL_BUILD_AD_REQUEST_SYNC =
  `${MANUAL_BUILD_AD_REQUEST}.sync` as const;
export const MANUAL_CANCEL_BUILD_AD_REQUEST_SYNC =
  `${MANUAL_CANCEL_BUILD_AD_REQUEST}.sync` as const;
export const MANUAL_SYNC_REQUEST_SYNC = `${MANUAL_SYNC_REQUEST}.sync` as const;
export const NOTICE_AT_DEADLINE_SYNC = `${NOTICE_AT_DEADLINE}.sync` as const;
export const NOTICE_CANCELLED_SYNC = `${NOTICE_CANCELLED}.sync` as const;
export const NOTICE_CONFIRMED_SYNC = `${NOTICE_CONFIRMED}.sync` as const;
export const NOTICE_CREATED_SYNC = `${NOTICE_CREATED}.sync` as const;
export const NOTICE_EDITED_SYNC = `${NOTICE_EDITED}.sync` as const;

export const PROOF_WEBHOOK_EVENT = {
  TRANSACTION_RELEASED: 'proof.transaction.released' as const
};

export const SYNC_EVENT_TYPES: SyncEvent['type'][] = [
  CUSTOM_ID_UPDATED_SYNC,
  INVOICE_CREATED_SYNC,
  INVOICE_PAID_OUTSIDE_SYNC,
  INVOICE_PAYMENT_INITIATED_SYNC,
  INVOICE_PAYMENT_PROCESSED_MANUAL_SYNC,
  INVOICE_PAYMENT_PROCESSED_SYNC,
  INVOICE_REFUNDED_SYNC,
  INVOICE_UPFRONT_PAYMENT_WAIVER_SYNC,
  MANUAL_BUILD_AD_REQUEST_SYNC,
  MANUAL_CANCEL_BUILD_AD_REQUEST_SYNC,
  MANUAL_SYNC_REQUEST_SYNC,
  NOTICE_AT_DEADLINE_SYNC,
  NOTICE_CANCELLED_SYNC,
  NOTICE_CONFIRMED_SYNC,
  NOTICE_CREATED_SYNC,
  NOTICE_EDITED_SYNC
];

export type OrderSyncTriggerEventType =
  | typeof ORDER_CANCELLED
  | typeof ORDER_EDITED
  | typeof ORDER_INVOICE_CREATED
  | typeof ORDER_INVOICE_PAID
  | typeof ORDER_INVOICE_PAID_OUT_OF_PLATFORM
  | typeof ORDER_INVOICE_REFUNDED
  | typeof ORDER_MANUAL_SYNC_REQUEST
  | typeof ORDER_NEWSPAPER_ORDER_AT_DEADLINE
  | typeof ORDER_NEWSPAPER_ORDER_CONFIRMED
  | typeof ORDER_NEWSPAPER_ORDER_DELETED;
export const ORDER_SYNC_TRIGGER_EVENT_TYPES: OrderSyncTriggerEventType[] = [
  ORDER_CANCELLED,
  ORDER_EDITED,
  ORDER_INVOICE_CREATED,
  ORDER_INVOICE_PAID,
  ORDER_INVOICE_PAID_OUT_OF_PLATFORM,
  ORDER_INVOICE_REFUNDED,
  ORDER_MANUAL_SYNC_REQUEST,
  ORDER_NEWSPAPER_ORDER_AT_DEADLINE,
  ORDER_NEWSPAPER_ORDER_CONFIRMED,
  ORDER_NEWSPAPER_ORDER_DELETED
];
export const isOrderSyncTriggerEventType = (
  type: OrderEvent['type']
): type is (typeof ORDER_SYNC_TRIGGER_EVENT_TYPES)[number] => {
  return ORDER_SYNC_TRIGGER_EVENT_TYPES.some(t => t === type);
};

type StatusChangeType =
  | typeof RUN_STATUS_CHANGE
  | typeof NOTARIZATION_STATUS_CHANGE
  | typeof LEDGER_ITEM_STATUS_CHANGE;

type OrderEventType =
  | typeof ORDER_INVOICE_REFUNDED
  | typeof ORDER_INVOICE_CREATED
  | typeof ORDER_INVOICE_PAID
  | typeof ORDER_INVOICE_PAID_OUT_OF_PLATFORM
  | typeof ORDER_INVOICE_PAYMENT_NOTE
  | typeof ORDER_EDITED
  | typeof ORDER_CANCELLED
  | typeof ORDER_OBITUARY_VERIFIED
  | typeof ORDER_NEWSPAPER_ORDER_CONFIRMED
  | typeof ORDER_NEWSPAPER_ORDER_COMPLETED
  | typeof ORDER_NEWSPAPER_ORDER_DELETED
  | typeof ORDER_NEWSPAPER_ORDER_AT_DEADLINE
  | typeof ORDER_MANUAL_SYNC_REQUEST;

export type ProcessableEventType =
  | typeof EEDITION_UPLOADED
  | typeof BRIEF_BATCH_PDFS_UPLOADED
  | typeof RUN_VERIFICATION_INITIATED
  | typeof NOTICE_RECONCILE_RUNS
  | typeof REALTIME_ELASTIC_SYNC
  | typeof INBOX_AUTOMATION_NOTICE_UPLOAD_REQUEST
  | typeof INBOX_AUTOMATION_CANCELLATION_REQUEST
  | typeof INBOX_AUTOMATION_CONFIRM_PUBLICATION_REQUEST
  | typeof INBOX_AUTOMATION_AFFIDAVIT_REQUEST
  | typeof INBOX_AUTOMATION_AFFIDAVIT_UPLOAD
  | typeof INBOX_AUTOMATION_INTENT_REQUEST
  | typeof INBOX_AUTOMATION_INTENT_TEARSHEET_REQUEST
  | typeof INBOX_AUTOMATION_INTENT_AFFIDAVIT_REQUEST
  | typeof INBOX_AUTOMATION_INTENT_INVOICE_REQUEST
  | typeof INBOX_AUTOMATION_INTENT_PROOF_REQUEST
  | typeof INBOX_AUTOMATION_INTENT_PUBLICATION_DATES_REQUEST
  | typeof INBOX_AUTOMATION_INTENT_NEW_PUBLICATION_REQUEST
  | typeof LEDGER_ITEM_RECEIPT_READY
  | typeof MONTHLY_STATEMENT_EVENT
  | typeof INBOX_AUTOMATION_LOG_EVENT
  | typeof INBOX_AUTOMATION_TRIGGER_EVENT
  | typeof PLACEMENT_FEE_BILLING_TRIGGER
  | typeof APPROVE_BUILT_AD_REQUEST
  | typeof NOTICES_BUILT_AD_REVIEW_REQUEST
  | typeof AUTOMATED_BUILD_AD_REQUEST
  | typeof PROOF_WEBHOOK_EVENT.TRANSACTION_RELEASED
  | typeof INBOX_AUTOMATION_PLACEMENT_REQUEST
  | typeof INBOX_AUTOMATION_LINK_THREAD_REQUEST
  | typeof STRIPE_OPERATION
  | typeof PUBLIC_API_PLACEMENT_REQUEST;

export type EventType =
  | typeof NOTICE_SYNC_EVENT
  | typeof NOTICE_SYNC_FAILURE_EVENT
  | typeof NOTICE_SYNC_RESPONSE_FILE_RECEIVED_EVENT
  | typeof NOTICE_SUBMITTED_EVENT
  | typeof INVOICE_MAIL_SENT_EVENT
  | typeof NOTICE_CONFIRMED
  | typeof NOTICE_EDITED
  | typeof AFFIDAVIT_UPLOADED
  | typeof AFFIDAVIT_UPLOAD_REJECTED
  | typeof CUSTOM_ID_UPDATED
  | typeof DESIGN_NOTES_UPDATED
  | typeof AFFIDAVIT_RE_UPLOADED
  | typeof NOTICE_TRANSFERRED
  | typeof NOTICE_REVERSE_TRANSFER_FEE
  | typeof NOTICE_REVERSE_TRANSFER_REFUND
  | typeof NOTICE_NOTE_ADDED
  | typeof NOTICE_CANCELLED
  | typeof NOTICE_CANCELLATION_REQUESTED
  | typeof NOTICE_NEWSPAPER_REQUESTED
  | typeof NOTICE_CANCELLED_AND_REFUNDED
  | typeof INVOICE_CANCELLED
  | typeof INVOICE_REFUNDED
  | typeof INVOICE_REMINDER_SENT
  | typeof INVOICE_RE_CREATED
  | typeof INVOICE_PAYMENT_PROCESSED
  | typeof INVOICE_PAYMENT_INITIATED
  | typeof INVOICE_PAYMENT_PROCESSED_MANUAL
  | typeof INVOICE_PAYMENT_NOTE
  | typeof DEADLINE_OVERRIDE_EVENT
  | typeof NOTICE_SYNC_RESPONSE
  | typeof NOTICE_SYNC_RESPONSE_FILE_RECEIVED_EVENT
  | typeof INVOICE_PAID_OUTSIDE
  | typeof INVOICE_CREATED
  | typeof NOTICE_AFFILIATE_CLICKED
  | typeof AFFIDAVIT_NOTARIZATION_INITIATED
  | typeof AFFIDAVIT_NOTARIZATION_SUCCESS
  | typeof AFFIDAVIT_NOTARIZATION_FAILED
  | typeof INVITATION_CREATED
  | typeof AUTO_EEDITION_UPLOAD_SUCCESS
  | typeof AFFIDAVIT_EEDITION_SCRAPER_INITIATED
  | typeof NOTICE_AT_DEADLINE
  | typeof NEWSPAPER_AT_DEADLINE
  | typeof INVOICE_CREATION_INITIATED
  | typeof INVOICE_CREATION_FAILED
  | typeof NEWSPAPER_EXTERNAL_UPLOAD
  | typeof NOTICE_CONFIRMED_SYNC
  | typeof NOTICE_EDITED_SYNC
  | typeof CUSTOM_ID_UPDATED_SYNC
  | typeof NOTICE_CANCELLED_SYNC
  | typeof NOTICE_AT_DEADLINE_SYNC
  | typeof INVOICE_CREATED_SYNC
  | typeof INVOICE_PAYMENT_INITIATED_SYNC
  | typeof INVOICE_PAYMENT_PROCESSED_SYNC
  | typeof INVOICE_PAYMENT_PROCESSED_MANUAL_SYNC
  | typeof INVOICE_PAID_OUTSIDE_SYNC
  | typeof NOTICE_CREATED
  | typeof NOTICE_CREATED_SYNC
  | typeof INVOICE_UPFRONT_PAYMENT_WAIVER
  | typeof INVOICE_UPFRONT_PAYMENT_WAIVER_SYNC
  | typeof DISPLAY_SITE_UPLOAD
  | typeof NOTICE_DATA_EXTRACTED
  | typeof MANUAL_SYNC_REQUEST
  | typeof MANUAL_SYNC_REQUEST_SYNC
  | typeof MANUAL_BUILD_AD_REQUEST
  | typeof USER_REGISTERED
  | typeof MANUAL_BUILD_AD_REQUEST_SYNC
  | typeof MANUAL_CANCEL_BUILD_AD_REQUEST
  | typeof MANUAL_CANCEL_BUILD_AD_REQUEST_SYNC
  | typeof INVOICE_REFUNDED_SYNC
  | typeof NOTICE_ASYNC_DESIGN_FINALIZED
  | typeof INBOX_AUTOMATION_DRAFT_CREATED
  | typeof INBOX_AUTOMATION_DRAFT_SENT
  | typeof INBOX_AUTOMATION_CONTEXT_CALL
  | typeof ADMIN_OPERATION
  | typeof TRANSACTIONAL_EMAIL_EVENT
  | typeof NEWSPAPER_AUTOMATIC_BULK_EXPORT
  | typeof PAPER_CHECK_CREATED
  | typeof PUBLICATION_ISSUE_STATUS_CHANGE
  | typeof PUBLICATION_ISSUE_SECTION_STATUS_CHANGE
  | typeof INBOX_AUTOMATION_DRAFT_REQUEST
  | typeof PUBLICATION_ISSUE_READY_FOR_PAGINATION
  | typeof STRIPE_ID_UPDATED
  | typeof PUBLISHER_GLOBAL_RATE_SETTINGS_UPDATED
  | typeof PUBLISHER_ADDITIONAL_FEE_SETTINGS_UPDATED
  | typeof EMAIL_UPDATED
  | typeof INVOICE_REMINDER_DUE
  | OrderEventType
  | StatusChangeType
  | ProcessableEventType
  | typeof ADJUDICATION_AREA_SET
  | typeof PROOF_WEBHOOK_EVENT.TRANSACTION_RELEASED;
export type EventBase = EData & {
  createdAt: FirebaseTimestamp;
  type: EventType;

  /** If true, the event is hidden in the Activity log */
  hidden?: boolean;
};

/**
 * A processable event is an event that is handled by a scheduled cron job rather than a webhook/event listener.
 * Its data include information about the number of processing attempts and whether the even has been successfully processed.
 * A processable event is considered "processed" if it has either been successfully handled by the cron or has reached the maximum number of processing attempts.
 *
 * See the following link for a more in-depth explanation of why we do this: https://blog.sequin.io/events-not-webhooks/
 */
export enum ProcessableStatus {
  IN_PROGRESS = 'in_progress',
  SUCCESS = 'success',
  PERMANENT_FAILURE = 'permanent_failure'
}
export const isTerminalProcessableStatus = (
  status: ProcessableStatus | null | undefined
) => {
  return (
    status === ProcessableStatus.SUCCESS ||
    status === ProcessableStatus.PERMANENT_FAILURE
  );
};

interface ProcessableEventBase extends EventBase {
  /**
   * Null if the event is not yet processed; processable events should be initiated with a `null` value in this field
   */
  processedAt: FirebaseTimestamp | null;

  /**
   * Each processable event will have a cron that specifies the maximum number of processing attempts before the event is considered failed
   */
  processAttempts: number;

  /**
   * An optional property that some processors use to delay processing of the
   * event until a certain time.
   */
  processAfter?: FirebaseTimestamp;

  /**
   * Optional status set when the event is in-progress or reaches a terminal state.
   * Absent on legacy events created before this field was introduced.
   */
  status?: ProcessableStatus | null;

  /**
   * Timestamp of the most recent status change. Used to detect and recover
   * from stuck IN_PROGRESS locks caused by function timeouts.
   */
  statusChangedAt?: FirebaseTimestamp | null;

  /** Optional human-readable message accompanying the status. */
  statusMessage?: string;

  /**
   * When `true`, the immediate fan-out processor (Firestore onCreate trigger) will skip this
   * event. Processing will be handled exclusively by a query-based (cron/polling) processor.
   * Omitting this field or setting it to `false` preserves the default behaviour where the
   * onCreate trigger initiates processing right away.
   */
  skipImmediateProcessing?: boolean;

  type: ProcessableEventType;
}

/**
 * An event that has side effects is an event that triggers additional events on creation via the
 * `processEventCreation` function. This base type is used to add common properties to all events
 * that have side effects.
 */
type EventWithSideEffectBase = EventBase & {
  /**
   * The purpose of this flag is two-fold.
   * 1. It helps us to identify events that have side effects, because it is a required property
   * that we must explicitly define when creating relevant events.
   * 2. It allows us to build a mechanism to skip side effects when we want to create an event that
   * typically has side effects, but we have some reason to want to avoid them.
   *
   * This is possibly `undefined`, because some events existed before they had side effects, so
   * there's no guarantee all relevant event documents will have this flag. Typing it as
   * `boolean | undefined` instead of making it optional (`handleSideEffects?: boolean`) ensures
   * that we are forced to add it when creating new documents.
   */
  handleSideEffects: boolean | undefined;
};

/**
 * Interface representing events that have a "notice" as a property. Used as a method
 * of determining if an event can be cross-referenced against a paper, for work like
 * Column Express
 */
export interface NoticeEvent extends EventBase {
  notice: ERef<ENotice>;
}

export interface NoticeCreatedEvent extends EventBase {
  type: typeof NOTICE_CREATED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    createdBy?: ERef<EUser>;
    publicationDates: FirebaseTimestamp[];
  };
}

export interface NoticeSyncEvent extends EventBase {
  type: typeof NOTICE_SYNC_EVENT;
  notice: ERef<ENotice>;
  data: {
    storagePath?: string;
    filename?: string;
    trigger?: EIntegrationTrigger;
  };
}

export interface NoticeSyncFailure extends EventBase {
  type: typeof NOTICE_SYNC_FAILURE_EVENT;
  notice: ERef<ENotice>;
  data: {
    error: string;
    trigger?: EIntegrationTrigger;
  };
}

export interface NoticeSyncResponse extends EventBase {
  type: typeof NOTICE_SYNC_RESPONSE;
  notice: ERef<ENotice>;
  data: {
    response: string;
  };
}

export interface NoticeSyncResponseFileReceived extends EventBase {
  type: typeof NOTICE_SYNC_RESPONSE_FILE_RECEIVED_EVENT;
  notice: ERef<ENotice>;
  data: {
    filePath: string;
    trigger?: IntegrationTrigger;
  };
}

export interface NoticeConfirmedEvent extends EventBase {
  type: typeof NOTICE_CONFIRMED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    confirmedBy: ERef<EUser> | null;
  };
}

export interface NoticeNewspaperRequested extends EventBase {
  type: typeof NOTICE_NEWSPAPER_REQUESTED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
  };
}

export interface NoticeSubmittedEvent extends EventBase {
  type: typeof NOTICE_SUBMITTED_EVENT;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    publicationDates: FirebaseTimestamp[];
    submittedNoticeData?: Partial<ENotice>;
  };
}

export type OrderInvoicePaidEvent = EventWithSideEffectBase & {
  type: typeof ORDER_INVOICE_PAID;
  order: ERef<Order>;
  data: {
    product: Product;
    invoice: ERef<OrderInvoice>;
  };
};

export interface NoticeTransferredEvent extends EventWithSideEffectBase {
  type: typeof NOTICE_TRANSFERRED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice?: ERef<PublicNoticeInvoice>;
  };
}

export interface NoticeReverseTransferFeeEvent extends EventWithSideEffectBase {
  type: typeof NOTICE_REVERSE_TRANSFER_FEE;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice: ERef<PublicNoticeInvoice>;
  };
}

export interface NoticeReverseTransferRefundEvent
  extends EventWithSideEffectBase {
  type: typeof NOTICE_REVERSE_TRANSFER_REFUND;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice: ERef<PublicNoticeInvoice>;
  };
}

export interface NoticeNoteAdded extends EventBase {
  type: typeof NOTICE_NOTE_ADDED;
  notice: ERef<ENotice>;
  data: {
    note: ERef<Note>;
  };
}

export interface InvoiceMailSentEvent extends EventBase {
  type: typeof INVOICE_MAIL_SENT_EVENT;
  notice: ERef<ENotice>;
  data: {
    lobUrl: string;
  };
}
export interface NoticeEditedEvent extends EventBase {
  type: typeof NOTICE_EDITED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    editedBy?: ERef<EUser>;
    beforeData?: ENotice;
    afterData?: ENotice;
  };
}

export interface NoticeCancelledEvent extends EventBase {
  type: typeof NOTICE_CANCELLED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
  };
}

export interface NoticeCancellationRequestedEvent extends EventBase {
  type: typeof NOTICE_CANCELLATION_REQUESTED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    requestedBy: ERef<EUser>;
    cancellationReason: string;
  };
}

export interface NoticeDataExtractedEvent extends EventBase {
  type: typeof NOTICE_DATA_EXTRACTED;
  notice: ERef<EPublicNotice>;
  data: {
    classifiedType: string;
    extractedEntities: Record<string, any>[];
    text: string;
  };
}

export interface AffidavitUploadedEvent extends EventBase {
  type: typeof AFFIDAVIT_UPLOADED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    uploadedBy?: ERef<EUser>;
  };
}

export interface AffidavitUploadRejectedEvent extends EventBase {
  type: typeof AFFIDAVIT_UPLOAD_REJECTED;
  notice: ERef<ENotice>;
  data: {
    affidavitStoragePath: string;
    reason: string;
  };
}

export interface PublisherGlobalRateSettingsUpdated extends EventBase {
  type: typeof PUBLISHER_GLOBAL_RATE_SETTINGS_UPDATED;
  publisher: ERef<EOrganization>;
  data: {
    changedBy: ERef<EUser>;
    before: Partial<EOrganization>;
    after: Partial<EOrganization>;
  };
}

export interface PublisherAdditionalFeeSettingsUpdated extends EventBase {
  type: typeof PUBLISHER_ADDITIONAL_FEE_SETTINGS_UPDATED;
  publisher: ERef<EOrganization>;
  data: {
    changedBy: ERef<EUser>;
    before: Partial<EOrganization>;
    after: Partial<EOrganization>;
  };
}

export interface CustomIdUpdatedEvent extends EventBase {
  type: typeof CUSTOM_ID_UPDATED;
  notice: ERef<ENotice>;
  data: {
    newCustomId: string;
    oldCustomId?: string;
  };
}

export interface DesignNotesUpdatedEvent extends EventBase {
  type: typeof DESIGN_NOTES_UPDATED;
  notice: ERef<ENotice>;
  data: {
    message: string;
    editedAt: FirebaseTimestamp;
    editedBy?: ERef<EUser>;
  };
}

export interface AffidavitReUploadedEvent extends EventBase {
  type: typeof AFFIDAVIT_RE_UPLOADED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    uploadedBy?: ERef<EUser>;
  };
}

/**
 * @deprecated Use NoticeCancelledEvent and InvoiceRefundedEvent instead
 */
export interface NoticeCancelledAndRefundedEvent extends EventBase {
  type: typeof NOTICE_CANCELLED_AND_REFUNDED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice?: ERef<EInvoice>;
  };
}

export interface InvoiceReminderSentEvent extends EventBase {
  type: typeof INVOICE_REMINDER_SENT;
  notice: ERef<ENotice>;
  data: {
    filer: ERef<EUser>;
    invoice: ERef<EInvoice>;
    user: ERef<EUser>;
    byMail?: boolean;
    email?: string;
  };
}

export interface InvoiceCancelledEvent extends EventBase {
  type: typeof INVOICE_CANCELLED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    cancelledBy?: ERef<EUser>;
  };
}

export interface InvoiceRefundedEvent extends EventWithSideEffectBase {
  type: typeof INVOICE_REFUNDED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    invoice: ERef<EInvoice>;
    filer: ERef<EUser>;
    cancelledBy?: ERef<EUser>;
    refundedBy?: ERef<EUser>;
    refundAmount: number;
    refundReason?: string;
  };
}

export type OrderInvoiceCreatedEvent = EventWithSideEffectBase & {
  type: typeof ORDER_INVOICE_CREATED;
  order: ERef<Order>;
  data: {
    orderInvoice: ERef<OrderInvoice>;
    createdBy: ERef<EUser> | null;
  };
};

export type OrderInvoiceRefundedEvent = EventWithSideEffectBase & {
  type: typeof ORDER_INVOICE_REFUNDED;
  order: ERef<Order>;
  data: {
    invoice: ERef<OrderInvoice>;
    cancelledBy?: ERef<EUser>;
    refundedBy?: ERef<EUser>;
    refundAmount: number;
    refundReason?: string;
  };
};
export type OrderInvoicePaidOutOfPlatformEvent = EventWithSideEffectBase & {
  type: typeof ORDER_INVOICE_PAID_OUT_OF_PLATFORM;
  order: ERef<Order>;
  data: {
    invoice: ERef<OrderInvoice>;
    product: Product;
  };
};

export interface OrderInvoicePaymentNoteEvent extends EventBase {
  type: typeof ORDER_INVOICE_PAYMENT_NOTE;
  order: ERef<Order>;
  data: {
    invoice: ERef<OrderInvoice>;
    /**
     * The publisher user
     */
    user: ERef<EUser>;
    note: string;
  };
}

export type OrderEditedEvent = EventWithSideEffectBase & {
  type: typeof ORDER_EDITED;
  order: ERef<Order>;
  data: {
    previousVersion: number;
    newVersion: number;
    /**
     * If a user is not logged in when they edit an order,
     * this field will not be defined.
     */
    editedBy?: ERef<EUser>;
  };
};

export type OrderCancelledEvent = EventWithSideEffectBase & {
  type: typeof ORDER_CANCELLED;
  order: ERef<Order>;
  data: {
    cancelledByEmail: string;
    cancellationReason: string;
  };
};

export interface OrderObituaryVerifiedEvent extends EventBase {
  type: typeof ORDER_OBITUARY_VERIFIED;
  order: ERef<Order>;
  data:
    | {
        verificationMethod: 'manual';
        /**
         * If the user verifying the obituary is logged in, this will be a ref.
         * If the user is anonymous, it will be their email.
         */
        verifiedBy: ERef<EUser> | string;
      }
    | {
        verificationMethod: 'automatic';
      };
}

export interface OrderNewspaperOrderCompletedEvent
  extends EventWithSideEffectBase {
  type: typeof ORDER_NEWSPAPER_ORDER_COMPLETED;
  order: ERef<Order>;
  data: {
    newspaperOrder: ERef<NewspaperOrder>;
  };
}

export type OrderNewspaperOrderConfirmedEvent = EventWithSideEffectBase & {
  type: typeof ORDER_NEWSPAPER_ORDER_CONFIRMED;
  order: ERef<Order>;
  data: {
    confirmedBy: ERef<EUser>;
    newspaperOrder: ERef<NewspaperOrder>;
  };
};

export type OrderNewspaperOrderDeletedEvent = EventWithSideEffectBase & {
  type: typeof ORDER_NEWSPAPER_ORDER_DELETED;
  order: ERef<Order>;
  data: {
    newspaperOrder: ERef<NewspaperOrder>;
  };
};

export type OrderNewspaperOrderAtDeadlineEvent = EventWithSideEffectBase & {
  type: typeof ORDER_NEWSPAPER_ORDER_AT_DEADLINE;
  order: ERef<Order>;
  data: {
    newspaperOrder: ERef<NewspaperOrder>;
    deadlineTime: FirebaseTimestamp;
    publicationDate: string;
  };
};

export type OrderManualSyncRequestEvent = EventWithSideEffectBase & {
  type: typeof ORDER_MANUAL_SYNC_REQUEST;
  order: ERef<Order>;
  data: {
    requestedBy: ERef<EUser>;
    publisher: ERef<EOrganization>;
  };
};

export interface UserRegisteredEvent extends EventBase {
  type: typeof USER_REGISTERED;
  user: ERef<EUser>;
  data: {
    occupationKey: keyof typeof OccupationType;
  };
}

export interface InvoiceReCreatedEvent extends EventBase {
  type: typeof INVOICE_RE_CREATED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice?: ERef<EInvoice>;
  };
}

export interface InvoicePaymentProcessEvent extends EventWithSideEffectBase {
  type: typeof INVOICE_PAYMENT_PROCESSED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    invoice: ERef<EInvoice>;
    filer: ERef<EUser>;
    paymentMethod?: PaymentMethods;
  };
}

export interface InvoicePaymentInitiatedEvent extends EventBase {
  type: typeof INVOICE_PAYMENT_INITIATED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice?: ERef<EInvoice>;
  };
}

export interface InvoicePaymentProcessedManualEvent
  extends EventWithSideEffectBase {
  type: typeof INVOICE_PAYMENT_PROCESSED_MANUAL;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    invoice: ERef<EInvoice>;
    user?: ERef<EUser>;
  };
}

export interface InvoicePaymentNoteEvent extends EventBase {
  type: typeof INVOICE_PAYMENT_NOTE;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    invoice: ERef<EInvoice>;
    user: ERef<EUser>;
    note: string;
  };
}

export interface InvoicePaidOutsideEvent extends EventWithSideEffectBase {
  type: typeof INVOICE_PAID_OUTSIDE;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice?: ERef<EInvoice>;
  };
}

export type InvoiceCreateEvent = EventBase & {
  type: typeof INVOICE_CREATED;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice?: ERef<EInvoice>;
    createdBy?: ERef<EUser>;
  };
};

export interface InvoiceUpfrontPaymentWaiverEvent extends EventBase {
  type: typeof INVOICE_UPFRONT_PAYMENT_WAIVER;
  notice: ERef<ENotice>;
  data: {
    newspaper: ERef<EOrganization>;
    filer: ERef<EUser>;
    invoice?: ERef<EInvoice>;
  };
}

export interface DeadlineOverrideEvent extends EventBase {
  type: typeof DEADLINE_OVERRIDE_EVENT;
  data: {
    newspaper: ERef<EOrganization>;
  };
}

export interface NewspaperAtDeadlineEvent extends EventBase {
  type: typeof NEWSPAPER_AT_DEADLINE;
  newspaper: ERef<EOrganization>;
  data: {
    deadline: ERef<EDeadline>;
    deadlineTime: FirebaseTimestamp;
    firstPublicationDate: FirebaseTimestamp;
    relevantNotices: ERef<ENotice>[];
    isOverride: boolean;
  };
}

export interface NoticeAtDeadlineEvent extends EventBase {
  type: typeof NOTICE_AT_DEADLINE;
  notice: ERef<ENotice>;
  data: {
    deadline: ERef<EDeadline>;
    deadlineTime: FirebaseTimestamp;
    firstPublicationDate: FirebaseTimestamp;
    isOverride: boolean;
  };
}

export interface NoticeAffiliateClickedEvent extends EventBase {
  type: typeof NOTICE_AFFILIATE_CLICKED;
  notice: ERef<ENotice>;
  data: {
    targetURL: string;
    user?: ERef<EUser>;
  };
}

export interface EEditionUploadedEvent extends ProcessableEventBase {
  eedition: ERef<EEdition>;
  type: typeof EEDITION_UPLOADED;
  data: Record<string, unknown>;
}

/**
 * Fired by the admin "upload paginated brief PDFs" endpoint, one per upload
 * call. The processor downloads the PDFs from Storage, emails them to the
 * configured Front channel, and writes them into Lee's FileMage folder so
 * Lee's SFTP user sees them. `postProcessingMetadata` records
 * per-side-effect timestamps so retries don't double-send.
 */
export interface BriefBatchPdfsUploadedEvent extends ProcessableEventBase {
  type: typeof BRIEF_BATCH_PDFS_UPLOADED;
  data: {
    /** Brief batch source ('lee' today; other vendors will appear here). */
    source: 'lee';
    /** YYYY-MM-DD publication date — together with `source` keys the batch. */
    date: string;
    cadence?: BriefBatchCadence;
    /**
     * Cloud Storage paths of the PDFs this upload added. The order matches
     * the upload form-data order so attachment ordering in the email is
     * deterministic.
     */
    paginatedPdfStoragePaths: string[];
  };
  postProcessingMetadata?: {
    emailedAt?: FirebaseTimestamp;
    /**
     * Number of attachment-chunked emails that have been successfully sent
     * for this event. Incremented after each chunk so a mid-send failure
     * resumes from the next un-sent chunk on retry instead of re-sending
     * already-delivered chunks. When this equals `Math.ceil(pdfCount /
     * MAX_ATTACHMENTS_PER_EMAIL)`, every chunk is delivered and
     * `emailedAt` is stamped to mark the email step complete.
     */
    emailedChunkCount?: number;
    /**
     * Set when the PDFs have been written into Lee's FileMage home folder
     * (`legal-briefs/lee/...` on the FileMage bucket). Named `ftp` not
     * `sftp` because the destination is our own FileMage server, not an
     * external SFTP endpoint — we deposit bytes directly into the GCS
     * bucket that backs Lee's SFTP root.
     */
    ftpUploadedAt?: FirebaseTimestamp;
  };
}

export interface NoticeReconcileRunsEvent extends ProcessableEventBase {
  notice: ERef<ENotice>;
  type: typeof NOTICE_RECONCILE_RUNS;
  data: Record<string, unknown>;
}

export interface RunVerificationInitiatedEvent extends ProcessableEventBase {
  run: ERef<Run>;
  type: typeof RUN_VERIFICATION_INITIATED;
  data: {
    eedition: ERef<EEdition>;
  };
}

export interface AffidavitNotarizationInitiatedEvent extends EventBase {
  type: typeof AFFIDAVIT_NOTARIZATION_INITIATED;
  notice: ERef<ENotice>;
  data: {
    metadata: {
      notarizationVendor: AffidavitReconciliationSettings['notarizationVendor'];

      // used to track Stak transactions
      projectId?: number;

      // used to track Proof transactions
      transactionID?: string;
    };
  };
}

export interface AffidavitNotarizationSucceededEvent extends EventBase {
  affidavitURL: string;
  notice: ERef<ENotice>;
  data: Record<string, any>;
  type: typeof AFFIDAVIT_NOTARIZATION_SUCCESS;
}

export interface AffidavitNotarizationFailedEvent extends EventBase {
  type: typeof AFFIDAVIT_NOTARIZATION_FAILED;
  notice: ERef<ENotice>;
  data: Record<string, any>;
}

export interface TransactionalEmailEvent extends EventBase {
  type: typeof TRANSACTIONAL_EMAIL_EVENT;
  data: {
    notificationType: string;
    emailEvent: string;
    messageId: string;
    email: string;
    user: ERef<EUser>;
  };
}

export interface AutoEeditionUploadSuccess extends EventBase {
  type: typeof AUTO_EEDITION_UPLOAD_SUCCESS;
  data: {
    publisherId: string;
    publicNoticeId: string;
  };
}

export interface AffidavitEeditionScraperInitiated extends EventBase {
  type: typeof AFFIDAVIT_EEDITION_SCRAPER_INITIATED;
  data: {
    editionPortalURL: string;
    publisher: ERef<EOrganization>;
  };
}

export interface RealTimeElasticSync extends ProcessableEventBase {
  type: typeof REALTIME_ELASTIC_SYNC;
  data: {
    documentType: ElasticDocumentType;
    documentPath: string;
    documentId: string;
  };
}

export interface InvoiceCreationInitiatedEvent extends EventBase {
  type: typeof INVOICE_CREATION_INITIATED;
  notice: ERef<ENotice>;
  data: EventInvoiceData;
}

export interface InvoiceCreationFailedEvent extends EventBase {
  type: typeof INVOICE_CREATION_FAILED;
  notice: ERef<ENotice>;
}

export interface PaperCheckCreatedEvent extends EventWithSideEffectBase {
  type: typeof PAPER_CHECK_CREATED;
  paperCheck: ERef<PaperCheck>;
}

export interface PublicationIssueStatusChangeEvent
  extends EventWithSideEffectBase {
  type: typeof PUBLICATION_ISSUE_STATUS_CHANGE;
  publicationIssue: ERef<PublicationIssue>;
  newspaper: ERef<EOrganization>;
  data: {
    attachmentData: PublicationIssueAttachment[];
    beforeStatus: PublicationIssueStatus;
    afterStatus: PublicationIssueStatus;
    changedBy: ERef<EUser>;
  };
}

export interface PublicationIssueSectionStatusChangeEvent
  extends EventWithSideEffectBase {
  type: typeof PUBLICATION_ISSUE_SECTION_STATUS_CHANGE;
  publicationIssueSection: ERef<PublicationIssueSection>;
  publicationIssue: ERef<PublicationIssue>;
  newspaper: ERef<EOrganization>;
  data: {
    beforeStatus: PublicationIssueStatus;
    afterStatus: PublicationIssueStatus;
    changedBy: ERef<EUser> | null;
  };
}

export interface PublicationIssueReadyForPaginationEvent extends EventBase {
  type: typeof PUBLICATION_ISSUE_READY_FOR_PAGINATION;
  publicationIssue: ERef<PublicationIssue>;
  data: {
    slackMessageTS: string;
  };
}

export interface NewspaperExternalUploadEvent extends EventBase {
  type: typeof NEWSPAPER_EXTERNAL_UPLOAD;
  newspaper: ERef<EOrganization>;
  data: {
    fileStoragePath: string;
  };
}

export interface InvitationCreatedEvent extends EventBase {
  type: typeof INVITATION_CREATED;
  data: {
    invite: ERef<EInvite>;
  };
}

export interface DisplaySiteUploadEvent extends EventBase {
  type: typeof DISPLAY_SITE_UPLOAD;
  newspaper: ERef<EOrganization> | null;
  data: {
    /**
     * The main file uploaded for this event.
     */
    fileStoragePath: string;
    /**
     * Additional files accesses while processing the main file uploaded.
     */
    supportingFileStoragePaths: string[];
    /**
     * A numeric status corresponding to the value of an item in the DisplaySiteUploadStatus enum.
     */
    uploadStatus: number;
    /**
     * A human-readable description of the final result of the upload.
     */
    message: string;
  };
}

export interface ManualSyncRequestEvent extends EventBase {
  type: typeof MANUAL_SYNC_REQUEST;
  notice: ERef<ENotice>;
  data: {
    /**
     * The user who requested a manual sync
     */
    requestedBy: ERef<EUser>;

    /**
     * Whether created in app or Retool
     */
    inApp: boolean;
  };
}

export interface ApproveBuiltAdRequestEvent extends ProcessableEventBase {
  type: typeof APPROVE_BUILT_AD_REQUEST;
  data: {
    orderNumber: AffinityXOrderNumber;
  };
}

export interface ReviewBuiltAdEvent extends ProcessableEventBase {
  type: typeof NOTICES_BUILT_AD_REVIEW_REQUEST;
  data: {
    noticeId: string;
  };
}

export interface AutomatedBuildAdRequestEvent extends ProcessableEventBase {
  type: typeof AUTOMATED_BUILD_AD_REQUEST;
  data: {
    /**
     * An array of storage paths for the materials used to build the ad.
     * Initially will just be a single path to the input file.
     */
    materials: string[];
    templateUrl: string;
    numberOfColumns: number;
    /**
     * The estimated height of the final ad
     */
    approxHeightInInches: number;
    /**
     * The number of pages with which to build the ad
     */
    pageCount: number;
  };
}

export interface ManualBuildAdRequestEvent extends EventBase {
  type: typeof MANUAL_BUILD_AD_REQUEST;
  notice: ERef<ENotice>;
  data: {
    /**
     * The user who requested a manual sync
     */
    requestedBy: ERef<EUser>;
    /**
     * Whether created in app or Retool
     */
    inApp: boolean;
    /**
     * Unique order number to use when creating the AffinityX order
     */
    orderNumber: AffinityXOrderNumber;
    /**
     * The number of columns with which to build the ad
     */
    numberOfColumns: number;
    /**
     * The estimated height of the final ad
     */
    approxHeightInInches: number;
    /**
     * The number of pages with which to build the ad
     */
    pageCount: number;
  };
}

export interface ManualCancelBuildAdRequestEvent extends EventBase {
  type: typeof MANUAL_CANCEL_BUILD_AD_REQUEST;
  notice: ERef<ENotice>;
  data: {
    initialOrderRequest: ERef<ManualBuildAdRequestEvent>;
  };
}

export type ContextValue = { id: string; value: string };
export interface InboxAutomationContextCall extends EventBase {
  type: typeof INBOX_AUTOMATION_CONTEXT_CALL;
  data: {
    contextType:
      | 'notice-context'
      | 'user-context'
      | 'gtv-context'
      | 'publisher-context'
      | 'conversation-context'
      | 'similar-conversation-context'
      | 'policy-context'
      | 'conversation-context'
      | 'create-notice-draft'
      | 'attached-file-context'
      | 'load-attachments';
    messageId: string;
    context: ContextValue[];
    input: Record<string, string>;
  };
}

export const INBOX_AUTOMATION_PLACEMENT_REQUEST =
  'inbox.automation.notice.request';

export interface InboxAutomationPlacementRequest extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_PLACEMENT_REQUEST;
  data: {
    source: 'front' | 'kustomer';
    conversationId: string;
    placementRequestData: PlacementRequestData;
  };
}

export const INBOX_AUTOMATION_LINK_THREAD_REQUEST =
  'inbox.automation.link.thread.request';

export interface InboxAutomationLinkThreadRequest extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_LINK_THREAD_REQUEST;
  data: {
    source: 'front';
    conversationId: string;
  };
}

export interface InboxAutomationDraftRequest extends EventBase {
  type: typeof INBOX_AUTOMATION_DRAFT_REQUEST;
  data: {
    source: 'front' | 'kustomer';
    conversationId: string;
  };
}
export interface InboxAutomationDraftCreated extends EventBase {
  type: typeof INBOX_AUTOMATION_DRAFT_CREATED;
  data: {
    source: 'front' | 'kustomer' | 'chima';
    contextCalls: InboxAutomationContextCall['data'][];
    conversationId: string;
    inboundMessageEmail: string;
    embedding: string;
    messageId: string;
    category: string;
    response: string;
    prompt: string;
    model: string;
  };
}
export interface InboxAutomationDraftSent extends EventBase {
  type: typeof INBOX_AUTOMATION_DRAFT_SENT;
  data: {
    source: 'front' | 'kustomer' | 'chima';
    conversationId: string;
    responseMessageId: string;
    messageId: string;
    response: string;
    columnResponseEmail: string;
    authorEmail: string | undefined;
    tags: string[];
  };
}

export interface AdjudicationAreaUpdated extends EventBase {
  type: typeof ADJUDICATION_AREA_SET;
  publisher: ERef<EOrganization>;
  data: {
    changedBy: ERef<EUser>;
    before?: ERef<AdjudicationArea>[];
    after: ERef<AdjudicationArea>[];
  };
}

/**
 * Inbox automation processable events created upon receiving inbound Front webhook requests,
 * to be processed within processEmailAutomationRequests. Events correspond to emailed requests
 * (intents are classified using Front rules prior to the webhooks being sent to Column)
 * for the following actions:
 * (1) notice upload / creation,
 * (2) notice cancellation,
 * (3) tearsheet or confirm publication, and
 * (4) affidavit.
 * */

export type InboxAutomationEventPostProcessingMetadata = {
  noticeId?: string;
};

export interface InboxAutomationNoticeUploadRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_NOTICE_UPLOAD_REQUEST;
  data: {
    source: 'front';
    conversationId: string;
  };
  postProcessingMetadata?: InboxAutomationEventPostProcessingMetadata;
}

export interface InboxAutomationCancellationRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_CANCELLATION_REQUEST;
  data: {
    source: 'front';
    conversationId: string;
  };
  postProcessingMetadata?: InboxAutomationEventPostProcessingMetadata;
}

export interface InboxAutomationConfirmPublicationRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_CONFIRM_PUBLICATION_REQUEST;
  data: {
    source: 'front';
    conversationId: string;
  };
  postProcessingMetadata?: InboxAutomationEventPostProcessingMetadata;
}

export interface InboxAutomationAffidavitRequest extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_AFFIDAVIT_REQUEST;
  data: {
    source: 'front';
    conversationId: string;
  };
  postProcessingMetadata?: InboxAutomationEventPostProcessingMetadata;
}

export interface InboxAutomationAffidavitUpload extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_AFFIDAVIT_UPLOAD;
  data: {
    source: string;
    conversationId: string;
    noticeId: string;
    affidavitUrl: string;
  };
  postProcessingMetadata?: InboxAutomationEventPostProcessingMetadata;
}

export interface InboxAutomationIntentRequest extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_INTENT_REQUEST;
  data: {
    source: 'front';
    conversationId: string;
  };
  postProcessingMetadata?: InboxAutomationEventPostProcessingMetadata;
}

type InboxAutomationResolvedIntentRequestData = {
  conversationId: string;
  intentReason: string;
  noticeId: string | null;
};

export interface InboxAutomationIntentTearsheetRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_INTENT_TEARSHEET_REQUEST;
  data: InboxAutomationResolvedIntentRequestData;
}

export interface InboxAutomationIntentAffidavitRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_INTENT_AFFIDAVIT_REQUEST;
  data: InboxAutomationResolvedIntentRequestData;
}

export interface InboxAutomationIntentInvoiceRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_INTENT_INVOICE_REQUEST;
  data: InboxAutomationResolvedIntentRequestData;
}

export interface InboxAutomationIntentProofRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_INTENT_PROOF_REQUEST;
  data: InboxAutomationResolvedIntentRequestData;
}

export interface InboxAutomationIntentPublicationDatesRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_INTENT_PUBLICATION_DATES_REQUEST;
  data: {
    conversationId: string;
    intentReason: string;
    publisherDetermination: PublisherSelection | null;
    placeholders: Record<string, never>;
  };
}

export interface InboxAutomationIntentNewPublicationRequest
  extends ProcessableEventBase {
  type: typeof INBOX_AUTOMATION_INTENT_NEW_PUBLICATION_REQUEST;
  data: {
    conversationId: string;
    intentReason: string;
    placeholders: Record<string, never>;
  };
}

/**
 * A human has completed the async design for a notice and uploaded the
 * finalized asset.
 */
export type NoticeAsyncDesignFinalizedEvent = EventBase & {
  type: typeof NOTICE_ASYNC_DESIGN_FINALIZED;
  notice: ERef<ENotice>;
  data: {
    /** Number of pages designed. For a less-than-full-page ad this should be '1' */
    pages?: number;
  };
};

type StatusChange<
  T extends StatusChangeType,
  R,
  S,
  D extends Record<string, any>
> = EventBase & {
  type: T;
  ref: ERef<R>;
  status: S;
  statusChangedBy?: ERef<EUser>;
  data?: D;
};

export type RunStatusChange = StatusChange<
  typeof RUN_STATUS_CHANGE,
  Run,
  RunStatusType,
  {
    unverifiableReason?: string;
    eedition?: ERef<EEdition>;
  }
>;

export type NotarizationStatusChange = StatusChange<
  typeof NOTARIZATION_STATUS_CHANGE,
  Notarization,
  NotarizationStatus,
  {}
>;

export type LedgerItemStatusChange = StatusChange<
  typeof LEDGER_ITEM_STATUS_CHANGE,
  LedgerItem,
  LedgerItemStatus,
  {}
>;

export type SyncStep = {
  message: string | null;
  fileStoragePath: string | null;
};

export type SyncWarning = {
  inAppMessage: string;
  retoolOnlyMessage: string | null;
};

export type SyncTriggerEvent =
  | CustomIdUpdatedEvent
  | InvoiceCreateEvent
  | InvoicePaidOutsideEvent
  | InvoicePaymentInitiatedEvent
  | InvoicePaymentProcessedManualEvent
  | InvoicePaymentProcessEvent
  | InvoiceRefundedEvent
  | InvoiceUpfrontPaymentWaiverEvent
  | ManualBuildAdRequestEvent
  | ManualCancelBuildAdRequestEvent
  | ManualSyncRequestEvent
  | NoticeAtDeadlineEvent
  | NoticeCancelledEvent
  | NoticeConfirmedEvent
  | NoticeCreatedEvent
  | NoticeEditedEvent;

export type SyncEventType = `${SyncTriggerEvent['type']}.sync`;

export interface SyncEvent<
  T extends SyncTriggerEvent = SyncTriggerEvent,
  U extends SyncEventType = SyncEventType
> extends EventBase {
  type: U;
  newspaper: ERef<EOrganization>;
  notice: ERef<ENotice>;
  trigger: ERef<T>;
  data: {
    // A collection of messages and/or file paths for debugging
    syncSteps: SyncStep[];
    // References the SyncStatusType
    syncStatus: number;
    // A human-readable message to add context to the sync status, appears in app and in Retool
    syncMessage: string | null;
    // A human-readable internal message visible only in Retool
    retoolMessage: string | null;
    // A collection of human-readable warning messages for the `success_with_warning` status.
    syncWarnings: SyncWarning[];
    // If there's an error, capture it here for potential dev attention
    syncError: string | null;
    // When we expect a response, if any, from the publisher
    expectResponseInSeconds: number | null;
  };
}

export type OrderSyncTriggerEvent =
  | OrderCancelledEvent
  | OrderEditedEvent
  | OrderInvoiceCreatedEvent
  | OrderInvoicePaidEvent
  | OrderInvoicePaidOutOfPlatformEvent
  | OrderInvoiceRefundedEvent
  | OrderNewspaperOrderConfirmedEvent
  | OrderNewspaperOrderDeletedEvent
  | OrderNewspaperOrderAtDeadlineEvent
  | OrderManualSyncRequestEvent;

export interface AdminOperationEvent extends EventBase {
  type: typeof ADMIN_OPERATION;
  data: {
    /**
     * The email of the Column user.
     */
    columnUser: string;

    /**
     * The path of the Admin API endpoint called.
     */
    path: string;

    /**
     * The request body.
     */
    body: JSONObject;
  };
}

export enum BulkExportStatus {
  /**
   * There were notices relevant to the publication date, and the export was successful
   */
  EXPORT_SUCCESS = 'export_success',
  /**
   * There were no notices relevant to the publication date, so no export was performed
   */
  EXPORT_NO_OP = 'export_no_op',
  /**
   * There were notices relevant to the publication date, but the export failed
   */
  EXPORT_FAILED = 'export_failed'
}

export type DestinationLogsEmail = {
  type: 'email';
  to: string[];
  replyTo: string;
};

export type DestinationLogsFTP = {
  type: 'ftp';
  to: string;
  filePath: string;
};

export type DestinationLogs = DestinationLogsEmail | DestinationLogsFTP;

export interface AutomatedBulkExportEvent extends EventBase {
  type: typeof NEWSPAPER_AUTOMATIC_BULK_EXPORT;
  /**
   * The newspaper that the bulk export is for
   */
  newspaper: ERef<EOrganization>;
  data: {
    /**
     * The final status of the bulk export attempt
     */
    status: BulkExportStatus;
    /**
     * The notices included in the bulk export
     */
    notices: ERef<ENotice>[];
    /**
     * The relevant publication date for the bulk export (in YYYY-MM-DD format)
     */
    publicationDate: string;
    /**
     * URL to download the bulk export ZIP file, if generated.
     */
    zipUrl: string | null;
    /**
     * Information about where and how the bulk export was to be delivered, if
     * we have it.
     */
    destination: DestinationLogs | null;
    /**
     * The full AutomaticBulkDownloadRequest, so that we can retry
     * from the Admin table.
     */
    request: AutomaticBulkDownloadRequest;
  };
}

export interface StripeIdUpdatedEvent extends EventBase {
  type: typeof STRIPE_ID_UPDATED;
  data: {
    oldStripeId: string;
    newStripeId: string;
    updatedBy: string;
    userId?: string;
    orgId?: string;
  };
}

export interface EmailUpdatedEvent extends EventBase {
  type: typeof EMAIL_UPDATED;
  data: {
    oldEmail: string;
    newEmail: string;
    userId: string;
    allowedOrganizations?: ERef<EOrganization>[] | null;
  };
}

export type DocumentEventsSearchRequest = {
  /**
   * The type of document for the search e.g. notice, invoice
   * This currently only searches at the top level of the event record
   */
  documentType: EventRefDocumentType;

  /**
   * The id of the document for the search
   */
  documentId: string;
};

export type InboxAutomationProcessableEvent =
  | InboxAutomationNoticeUploadRequest
  | InboxAutomationCancellationRequest
  | InboxAutomationConfirmPublicationRequest
  | InboxAutomationAffidavitRequest
  | InboxAutomationAffidavitUpload
  | InboxAutomationIntentRequest
  | InboxAutomationIntentTearsheetRequest
  | InboxAutomationIntentAffidavitRequest
  | InboxAutomationIntentInvoiceRequest
  | InboxAutomationIntentProofRequest
  | InboxAutomationIntentPublicationDatesRequest
  | InboxAutomationIntentNewPublicationRequest;

export type LedgerItemReceiptReadyEvent = ProcessableEventBase & {
  type: typeof LEDGER_ITEM_RECEIPT_READY;
  ledgerItem: ERef<LedgerItem>;
  data: {
    payorName: string;
    payorEmail: string;
    totalInDollars: string;
    lines: {
      noticeId: string;
      noticeUrl: string;
      noticeName: string;
      invoiceNumber: string;
      publisherName: string;
      amountInDollars: string;
    }[];
    receiptUrl: string;
  };
};

type OrganizationMonthlyStatementEventData = {
  payor: ERef<EOrganization>;
  payorType: 'organization';
  invoices: ERef<OrderInvoice>[];
};
type UserMonthlyStatementEventData = {
  payor: ERef<EUser>;
  payorType: 'user';
  invoices: ERef<OrderInvoice>[];
};
export type MonthlyStatementEvent = ProcessableEventBase & {
  type: typeof MONTHLY_STATEMENT_EVENT;
  data: OrganizationMonthlyStatementEventData | UserMonthlyStatementEventData;
};

export type InboxAutomationLogEvent = ProcessableEventBase & {
  type: typeof INBOX_AUTOMATION_LOG_EVENT;
  data: {
    /**
     * ZOD-validated event data from Front (via the `validateGenericFrontEvent`
     * function). For further validation and type narrowing, use a more specific
     * validation function (e.g. `validateFrontCommentEvent`).
     */
    frontEvent: FrontEvent;
  };
};

export type InboxAutomationTriggerEvent = ProcessableEventBase & {
  type: typeof INBOX_AUTOMATION_TRIGGER_EVENT;
  data: {
    /**
     * The event data from Front, validated by the `validateGenericFrontEvent`
     * function. For further validation and type narrowing, use a more specific
     * validation function (e.g. `validateFrontCommentEvent`).
     */
    frontEvent: FrontEvent;
    /**
     * Front events created by macro don't identify the macro itself, so we use an optional route parameter to identify the macro and store it here
     */
    macro?: string;
  };
};

export type PlacementFeeBillingTriggerEvent = ProcessableEventBase & {
  type: typeof PLACEMENT_FEE_BILLING_TRIGGER;
  notice: ERef<ENotice>;
  invoice: ERef<PublicNoticeInvoice>;
  data: {
    triggerEvent: ERef<
      | InvoicePaymentProcessEvent
      | InvoicePaymentProcessedManualEvent
      | InvoicePaidOutsideEvent
      | InvoiceRefundedEvent
    >;
  };
};

export type ProofTransactionReleasedProcessableEvent = ProcessableEventBase & {
  type: typeof PROOF_WEBHOOK_EVENT.TRANSACTION_RELEASED;
  data: {
    transaction_id: string;
    date_occurred: string;
    event_type: 'transaction.released';
  };
};

export type ProofProcessableEvent = ProofTransactionReleasedProcessableEvent;

type ApplicationFeeInvoiceOperationData = {
  operationType: StripeOperationType.APPLICATION_FEE_INVOICE;
  invoiceId: string;
  organizationId: string;
  stripeTransferId: string;
  applicationFeeInCents: number;
  /** Firestore transfer doc ID; when present the processor writes back the applicationFeeInvoiceId. */
  transferRefId?: string;
};

type PaperCheckReconciliationOperationData = {
  operationType: StripeOperationType.PAPER_CHECK_RECONCILIATION;
  stripeCheckId: string;
  invoiceId: string;
  customerEmail: string;
};

type PaymentProcessingFeeOperationData = {
  operationType: StripeOperationType.PAYMENT_PROCESSING_FEE;
  invoiceId: string;
};

type OrderPaymentProcessingFeeOperationData = {
  operationType: StripeOperationType.ORDER_PAYMENT_PROCESSING_FEE;
  invoiceId: string;
  orderId: string;
  newspaperOrderId: string;
};

type NoticeInvoiceCreationOperationData = EventInvoiceData & {
  operationType: StripeOperationType.NOTICE_INVOICE_CREATION;
};

type OrderInvoiceCreationOperationData = {
  operationType: StripeOperationType.ORDER_INVOICE_CREATION;
  orderId: string;
  version: number;
  createdBy: ERef<EUser> | null;
};

export type StripeOperationData =
  | ApplicationFeeInvoiceOperationData
  | PaperCheckReconciliationOperationData
  | PaymentProcessingFeeOperationData
  | OrderPaymentProcessingFeeOperationData
  | NoticeInvoiceCreationOperationData
  | OrderInvoiceCreationOperationData;

export type StripeOperationEvent = ProcessableEventBase & {
  type: typeof STRIPE_OPERATION;
  data: StripeOperationData;
};

export type ProcessableEvent =
  | EEditionUploadedEvent
  | BriefBatchPdfsUploadedEvent
  | NoticeReconcileRunsEvent
  | RunVerificationInitiatedEvent
  | RealTimeElasticSync
  | InboxAutomationProcessableEvent
  | LedgerItemReceiptReadyEvent
  | MonthlyStatementEvent
  | InboxAutomationLogEvent
  | InboxAutomationTriggerEvent
  | PlacementFeeBillingTriggerEvent
  | ApproveBuiltAdRequestEvent
  | ReviewBuiltAdEvent
  | AutomatedBuildAdRequestEvent
  | ProofProcessableEvent
  | InboxAutomationPlacementRequest
  | InboxAutomationLinkThreadRequest
  | StripeOperationEvent
  | PublicApiPlacementRequestEvent;
/**
 * This event type union represents event types we've explicitly marked as having side effects. By
 * side effects, we mean events that upon creation trigger additional actions to be taken, via our
 * `processEventCreation` function.
 *
 * This list is currently not exhaustive, introduced as a proof of concept in response to this
 * [comment](https://github.com/enotice-io/enotice-app/pull/20051#discussion_r1742660672), leaving *
 * a TODO: Update all event types with side effects to be part of this union, so that we can easily
 * identify them throughout the codebase.
 */
export type EventWithSideEffects =
  | PublicationIssueStatusChangeEvent
  | PublicationIssueSectionStatusChangeEvent
  | InvoicePaymentProcessEvent
  | InvoicePaymentProcessedManualEvent
  | InvoicePaidOutsideEvent
  | InvoiceRefundedEvent
  | NoticeTransferredEvent
  | NoticeReverseTransferFeeEvent
  | NoticeReverseTransferRefundEvent
  | PaperCheckCreatedEvent;

export type OrderEvent =
  | OrderInvoicePaidEvent
  | OrderInvoiceRefundedEvent
  | OrderInvoiceCreatedEvent
  | OrderInvoicePaidOutOfPlatformEvent
  | OrderInvoicePaymentNoteEvent
  | OrderEditedEvent
  | OrderCancelledEvent
  | OrderObituaryVerifiedEvent
  | OrderNewspaperOrderConfirmedEvent
  | OrderNewspaperOrderCompletedEvent
  | OrderNewspaperOrderDeletedEvent
  | OrderNewspaperOrderAtDeadlineEvent
  | OrderManualSyncRequestEvent;

export type Event =
  | NoticeSyncEvent
  | NoticeSyncFailure
  | NoticeSyncResponse
  | NoticeSyncResponseFileReceived
  | NoticeConfirmedEvent
  | NoticeNewspaperRequested
  | NoticeSubmittedEvent
  | EventWithSideEffects
  | NoticeNoteAdded
  | InvoiceMailSentEvent
  | NoticeEditedEvent
  | NoticeCancelledEvent
  | NoticeCancellationRequestedEvent
  | AffidavitUploadedEvent
  | AffidavitUploadRejectedEvent
  | CustomIdUpdatedEvent
  | DesignNotesUpdatedEvent
  | AffidavitReUploadedEvent
  | AffidavitNotarizationInitiatedEvent
  | AffidavitNotarizationSucceededEvent
  | AffidavitNotarizationFailedEvent
  | AutoEeditionUploadSuccess
  | AffidavitEeditionScraperInitiated
  | NoticeCancelledAndRefundedEvent
  | InvoiceReminderSentEvent
  | InvoiceCancelledEvent
  | InvoiceRefundedEvent
  | InvoiceReCreatedEvent
  | InvoicePaymentProcessEvent
  | InvoicePaymentInitiatedEvent
  | InvoicePaymentProcessedManualEvent
  | InvoicePaymentNoteEvent
  | InvoicePaidOutsideEvent
  | InvoiceCreateEvent
  | DeadlineOverrideEvent
  | NewspaperAtDeadlineEvent
  | NoticeAtDeadlineEvent
  | NoticeAffiliateClickedEvent
  | InvoiceCreationInitiatedEvent
  | InvoiceCreationFailedEvent
  | NewspaperExternalUploadEvent
  | SyncEvent<SyncTriggerEvent, SyncEventType>
  | NoticeCreatedEvent
  | InvoiceUpfrontPaymentWaiverEvent
  | DisplaySiteUploadEvent
  | NoticeDataExtractedEvent
  | ManualSyncRequestEvent
  | ManualBuildAdRequestEvent
  | ApproveBuiltAdRequestEvent
  | ManualCancelBuildAdRequestEvent
  | NoticeAsyncDesignFinalizedEvent
  | InboxAutomationDraftCreated
  | InboxAutomationDraftSent
  | InboxAutomationContextCall
  | AdminOperationEvent
  | AutomatedBulkExportEvent
  | PublisherGlobalRateSettingsUpdated
  | PublisherAdditionalFeeSettingsUpdated
  | InboxAutomationDraftRequest
  | RunStatusChange
  | PublicationIssueReadyForPaginationEvent
  | StripeIdUpdatedEvent
  | RealTimeElasticSync
  | ProcessableEvent
  | TransactionalEmailEvent
  | OrderEvent
  | NotarizationStatusChange
  | EmailUpdatedEvent
  | LedgerItemStatusChange
  | AdjudicationAreaUpdated
  | UserRegisteredEvent;
